import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { QuantiteSelector } from "./QuantiteSelector";
import { getTrancheChoixForQuantite, getValidQuantitesChoix, translateLibelleType } from "./Utils/achatUtils";
import "./TrancheSelector.css";

// #region FONCTION COMPOSANT
/**
 * AYB 08.09.26 Liste des tarifs "4" sous la ligne service.
 * 1 tarif = 1 ligne. +/- si min != max. Plusieurs tranches (paliers) = toujours 1 ligne, prix selon la quantité.
 * @category TrancheSelector
 */
function TrancheSelector(props) {
  //#region INITIALISATION
  const {
    clickable,
    onChange,
    onMaxReached,
    selectedIdTarif,
    selectedQuantite,
    symbole,
    tarifsChoix,
  } = props;
  const { t } = useTranslation();
  const options = Array.isArray(tarifsChoix) ? tarifsChoix : [];
  // #endregion

  // #region UTILS
  const getRowState = (option) => {
    const isActive = selectedIdTarif != null
      && Number(selectedQuantite) > 0
      && String(option.idTarif) === String(selectedIdTarif);
    const quantite = isActive && selectedQuantite
      ? selectedQuantite
      : (option.tranches[0]?.uniteMin ?? 1);
    const tranche = getTrancheChoixForQuantite(option.tranches, quantite) || option.tranches[0];
    const validQuantites = getValidQuantitesChoix(option.tranches);
    return {
      isActive,
      quantite,
      tranche,
      validQuantites,
      canAdjust: validQuantites.length > 1,
    };
  };

  const formatUnite = (option) => {
    //AYB 08.09.26 Afficher uniquement libelleType traduit (ex. "1 heure" / "1 hour").
    return translateLibelleType(option?.tarif?.libelleType, t, option?.tarif?.idUniteType);
  };

  const formatPrix = (tranche, option) => {
    const prix = Number.isFinite(parseFloat(tranche?.prix)) ? parseFloat(tranche.prix).toFixed(2) : tranche?.prix;
    const displaySymbole = option?.tarif?.symbole || symbole || "";
    return `${prix} ${displaySymbole}`.trim();
  };
  // #endregion

  // #region EVENTS
  const emitChange = (option, quantite) => {
    const tranche = getTrancheChoixForQuantite(option.tranches, quantite) || option.tranches[0];
    if (!tranche) return;
    onChange({
      tarif: option.tarif,
      actualTranche: tranche,
      quantite,
    });
  };

  const handleSelect = (event, option, quantite) => {
    event.stopPropagation();
    if (!clickable || !option) return;
    //AYB 10.09.26 Reclic sur la ligne active : retirer le tarif du panier.
    const isActive = selectedIdTarif != null
      && Number(selectedQuantite) > 0
      && String(option.idTarif) === String(selectedIdTarif);
    if (isActive) {
      event.currentTarget.blur();
      onChange({ tarif: option.tarif, actualTranche: null, quantite: 0 });
      return;
    }
    emitChange(option, quantite);
  };

  const handleQuantite = (event, option, quantite) => {
    event?.stopPropagation?.();
    if (!clickable || !option) return;
    emitChange(option, quantite);
  };
  // #endregion

  // #region INTERFACE
  if (!options.length) {
    return null;
  }

  const selectedOption = options.find((option) => String(option.idTarif) === String(selectedIdTarif));
  const selectedLibelle = selectedOption ? formatUnite(selectedOption) : "";

  return (
    <div className="TrancheSelector-List">
      <div className="TrancheSelector-Hint">
        {clickable || !selectedLibelle ? t("achat.choixTarif") : t("achat.tarifChoisi", { tarif: selectedLibelle })}
      </div>
      {options.map((option, index) => {
        const { isActive, quantite, tranche, validQuantites, canAdjust } = getRowState(option);
        const className = `TrancheSelector-Row${isActive ? " is-active" : ""}${clickable ? " is-clickable" : ""}`;

        return (
          <div
            key={option.idTarif ?? index}
            className={className}
            onClick={(event) => handleSelect(event, option, quantite)}
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
          >
            <span className="TrancheSelector-Row-Unite">
              {formatUnite(option)}
            </span>
            {canAdjust &&
              <div
                className="TrancheSelector-Row-Qty"
                onClick={(event) => event.stopPropagation()}
              >
                {clickable &&
                  <QuantiteSelector
                    compact
                    doShowButtons={clickable}
                    onChange={(value) => handleQuantite(null, option, value)}
                    onMaxReached={() => {
                      if (typeof onMaxReached === "function") {
                        onMaxReached(validQuantites[validQuantites.length - 1]);
                      }
                    }}
                    validQuantites={validQuantites}
                    value={quantite}
                  />
                }
              </div>
            }
            <span className="TrancheSelector-Row-Prix">{formatPrix(tranche, option)}</span>
          </div>
        );
      })}
    </div>
  );
  // #endregion
}
// #endregion

// #region PROPRIETES
/**
 * Type des propriétés de {@link TrancheSelector}
 * @typedef {Object} TrancheSelector.propTypes
 */
TrancheSelector.propTypes = {
  clickable: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onMaxReached: PropTypes.func,
  selectedIdTarif: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selectedQuantite: PropTypes.number,
  symbole: PropTypes.string,
  tarifsChoix: PropTypes.arrayOf(PropTypes.object),
};

TrancheSelector.defaultProps = {
  clickable: true,
  selectedIdTarif: null,
  selectedQuantite: 0,
  symbole: "€",
  tarifsChoix: [],
};
// #endregion

export { TrancheSelector };
